import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  botApi,
  presaleApi,
  vaultApi,
  type Presale,
  type VaultStatus,
  type RewardEpoch,
  type BotStatus,
} from '../../lib/presale-api';
import type { ReactElement } from 'react';

export function PresaleManagePage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const [presale, setPresale] = useState<Presale | null>(null);
  const [botStatus, setBotStatus] = useState<BotStatus | null>(null);
  const [vault, setVault] = useState<VaultStatus | null>(null);
  const [epochs, setEpochs] = useState<RewardEpoch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [autoSellTarget, setAutoSellTarget] = useState('1000');
  const [volumeTarget, setVolumeTarget] = useState('5');

  useEffect(() => {
    if (!id) {
      setError('Presale not found');
      setIsLoading(false);
      return;
    }

    Promise.all([
      presaleApi.get(id),
      vaultApi.getStatus(id).catch(() => null),
      vaultApi.getEpochs(id).catch(() => []),
    ])
      .then(([presaleData, vaultData, epochData]) => {
        setPresale(presaleData);
        setVault(vaultData);
        setEpochs(epochData);
        if (presaleData.forgeLaunchId) {
          return botApi.getStatus(presaleData.forgeLaunchId).catch(() => null);
        }
        return null;
      })
      .then(setBotStatus)
      .catch((err: Error) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [id]);

  const withAction = async (
    action: () => Promise<unknown>,
    successMessage: string
  ): Promise<void> => {
    setMessage(null);
    setError(null);
    try {
      await action();
      setMessage(successMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#c4f70e] border-t-transparent" />
      </div>
    );
  }

  if (error || !presale || !id) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="mb-4 text-red-400">{error || 'Presale not found'}</p>
        <Link to="/presale" className="text-sm text-[#c4f70e] hover:underline">
          Back to presales
        </Link>
      </div>
    );
  }

  const launchId = presale.forgeLaunchId;
  const latestEpoch = epochs[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Manage {presale.tokenName}</h1>
          <p className="mt-1 text-sm text-white/40">
            Launcher controls for bots, vault actions, and presale operations.
          </p>
        </div>
        <Link to={`/presale/${presale.id}`} className="text-sm text-[#c4f70e] hover:underline">
          View presale →
        </Link>
      </div>

      {message && (
        <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-4 text-sm text-green-400">
          {message}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="space-y-4 xl:col-span-2">
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Bot Controls</h2>
              <span className="text-xs text-white/30">
                {launchId ? `Launch ${launchId.slice(0, 8)}...` : 'Launch not created yet'}
              </span>
            </div>

            {!launchId ? (
              <p className="text-sm text-white/30">
                Bots become available after the presale has created a Forge launch.
              </p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-white/[0.06] bg-black/10 p-4">
                  <p className="mb-3 text-sm font-medium text-white">Auto-Sell</p>
                  <label className="mb-2 block text-xs text-white/40">Tokens per interval</label>
                  <input
                    value={autoSellTarget}
                    onChange={(event) => setAutoSellTarget(event.target.value)}
                    className="mb-3 w-full rounded-lg border border-white/[0.1] bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-[#c4f70e]/50 focus:outline-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        withAction(
                          () =>
                            botApi.configureAutoSell(launchId, {
                              mode: 'time_based',
                              sellAmount: Number(autoSellTarget),
                              intervalSec: 300,
                            }),
                          'Auto-sell configured'
                        )
                      }
                      className="flex-1 rounded-lg bg-[#c4f70e] px-3 py-2 text-xs font-semibold text-black transition-colors hover:bg-[#c4f70e]/90"
                    >
                      Start
                    </button>
                    <button
                      onClick={() =>
                        withAction(() => botApi.stopAutoSell(launchId), 'Auto-sell stopped')
                      }
                      className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-xs text-white/60 transition-colors hover:text-white"
                    >
                      Stop
                    </button>
                  </div>
                </div>

                <div className="rounded-lg border border-white/[0.06] bg-black/10 p-4">
                  <p className="mb-3 text-sm font-medium text-white">Volume Bot</p>
                  <label className="mb-2 block text-xs text-white/40">Target volume (SOL)</label>
                  <input
                    value={volumeTarget}
                    onChange={(event) => setVolumeTarget(event.target.value)}
                    className="mb-3 w-full rounded-lg border border-white/[0.1] bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-[#c4f70e]/50 focus:outline-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        withAction(
                          () =>
                            botApi.configureVolume(launchId, {
                              targetVolumeSol: Number(volumeTarget),
                              durationSec: 3600,
                            }),
                          'Volume bot configured'
                        )
                      }
                      className="flex-1 rounded-lg bg-[#c4f70e] px-3 py-2 text-xs font-semibold text-black transition-colors hover:bg-[#c4f70e]/90"
                    >
                      Start
                    </button>
                    <button
                      onClick={() =>
                        withAction(() => botApi.stopVolume(launchId), 'Volume bot stopped')
                      }
                      className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-xs text-white/60 transition-colors hover:text-white"
                    >
                      Stop
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Vault Admin</h2>
              <span className="text-xs text-white/30">
                {vault
                  ? `${Number(vault.currentBalanceSol).toFixed(4)} SOL available`
                  : 'Vault unavailable'}
              </span>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <button
                onClick={() =>
                  withAction(() => vaultApi.claimFees(id), 'Creator fees claimed into vault')
                }
                className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white/70 transition-colors hover:border-[#c4f70e]/30 hover:text-[#c4f70e]"
              >
                Claim Creator Fees
              </button>
              <button
                onClick={() =>
                  latestEpoch
                    ? withAction(
                        () => vaultApi.distribute(id, latestEpoch.id),
                        'Epoch distribution triggered'
                      )
                    : setError('No epoch available to distribute')
                }
                className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white/70 transition-colors hover:border-[#c4f70e]/30 hover:text-[#c4f70e]"
              >
                Distribute Latest Epoch
              </button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {[
                ['Collected', vault ? `${Number(vault.totalCollected).toFixed(4)} SOL` : '-'],
                ['Distributed', vault ? `${Number(vault.totalDistributed).toFixed(4)} SOL` : '-'],
                ['Epochs', String(epochs.length)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-white/[0.06] bg-black/10 p-4">
                  <p className="mb-1 text-xs text-white/30">{label}</p>
                  <p className="font-mono text-white">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">Launch Snapshot</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-white/40">Status</span>
                <span className="text-white">{presale.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">Raised</span>
                <span className="font-mono text-white">
                  {Number(presale.totalRaised).toFixed(2)} SOL
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">Buyers</span>
                <span className="text-white">
                  {presale.buyerCount}/{presale.maxBuyers}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/40">Epoch cadence</span>
                <span className="text-white">{presale.epochCadence}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">Danger Zone</h2>
            <button
              onClick={() =>
                withAction(() => presaleApi.cancel(id), 'Presale cancellation triggered')
              }
              className="w-full rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-400 transition-colors hover:bg-red-500/10"
            >
              Cancel Presale
            </button>
            <p className="mt-3 text-xs text-white/30">
              Cancel should only be used before the launch is live. Refund logic depends on the
              Forge presale backend state.
            </p>
          </div>

          {botStatus && (
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
              <h2 className="mb-4 text-lg font-semibold text-white">Bot Status</h2>
              <pre className="overflow-x-auto text-xs text-white/40">
                {JSON.stringify(botStatus, null, 2)}
              </pre>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
